export interface DrawingProgress {
  drawingUrl: string;
  image: Blob;
  /** Pixel dimensions used when the progress masks were created. */
  canvasWidth?: number;
  canvasHeight?: number;
  floodedPixels: number[];
  /** Pixels finalized by sweeping the sand away and therefore immutable. */
  filledPixels?: number[];
  progressPercent: number;
  updatedAt: number;
}

const databaseName = 'piskovani-drawings';
const databaseVersion = 1;
const storeName = 'progress';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB is not available.'));
      return;
    }
    const request = window.indexedDB.open(databaseName, databaseVersion);
    request.onerror = () => reject(request.error ?? new Error('Unable to open IndexedDB.'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(storeName)) {
        database.createObjectStore(storeName, { keyPath: 'drawingUrl' });
      }
    };
  });
}

export async function getDrawingProgress(drawingUrl: string): Promise<DrawingProgress | null> {
  try {
    const database = await openDatabase();
    return await new Promise((resolve, reject) => {
      const request = database.transaction(storeName, 'readonly').objectStore(storeName).get(drawingUrl);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve((request.result as DrawingProgress | undefined) ?? null);
    });
  } catch (error) {
    console.warn('Drawing progress could not be read.', error);
    return null;
  }
}

export async function getAllDrawingProgress(): Promise<DrawingProgress[]> {
  try {
    const database = await openDatabase();
    return await new Promise((resolve, reject) => {
      const request = database.transaction(storeName, 'readonly').objectStore(storeName).getAll();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result as DrawingProgress[]);
    });
  } catch (error) {
    console.warn('Drawing progress could not be listed.', error);
    return [];
  }
}

export async function saveDrawingProgress(progress: DrawingProgress): Promise<void> {
  try {
    const database = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const request = database.transaction(storeName, 'readwrite').objectStore(storeName).put(progress);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
    window.dispatchEvent(new CustomEvent('drawing-progress-updated'));
  } catch (error) {
    console.warn('Drawing progress could not be saved.', error);
  }
}

export async function deleteDrawingProgress(drawingUrl: string): Promise<void> {
  try {
    const database = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const request = database.transaction(storeName, 'readwrite').objectStore(storeName).delete(drawingUrl);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
    window.dispatchEvent(new CustomEvent('drawing-progress-updated'));
  } catch (error) {
    console.warn('Drawing progress could not be deleted.', error);
  }
}
